/**
 * "Where did I come from" for any page that's reachable from several places.
 * When a link into a page carries an origin, that page shows a breadcrumb (and
 * back-link) that returns to the exact place you clicked from instead of the
 * generic parent/catalog.
 *
 * The origin is UNIVERSAL: it encodes an arbitrary location — `{ to, params,
 * search, label }` — as a single opaque `origin` search param (base64url of
 * JSON). Any page can therefore be an origin for any other page. It rides in the
 * URL so it survives a refresh and a shared link, and because it's a single
 * token of `[A-Za-z0-9_-]` it round-trips cleanly through both TanStack's typed
 * search and the flat `URLSearchParams` list helpers (a raw string value would
 * get JSON-quoted by the router's serializer).
 *
 * Single-level by design: when a page captures "here" as an origin (see
 * `origin-context.tsx`), it strips any inbound `origin`/`lf` from the stored
 * search, so an origin never nests another origin and the URL stays bounded.
 *
 * `originLinkSearch` / `pageOriginSearch` remain as typed sugar for the common
 * record/page origins; they now produce the same generic encoded shape.
 */

import { z } from "zod";

type OriginKind = "person" | "role" | "certification" | "skill" | "competency";

const ORIGIN_KINDS: readonly OriginKind[] = [
  "person",
  "role",
  "certification",
  "skill",
  "competency",
];

/** Non-record pages a detail can return to, addressed by a stable key. */
type PageOriginKey = "org-health" | "not-started-certs" | "team" | "pto";

const PAGE_ORIGINS: Record<PageOriginKey, { to: string; label: string }> = {
  "org-health": { to: "/dashboard/org-health", label: "Org Health" },
  "not-started-certs": {
    to: "/dashboard/certifications/not-started",
    label: "Certs Not Started",
  },
  // Only the viewer's OWN team page. A manager-detail team page is addressed by
  // person id, which a fixed page origin cannot express — those links go
  // without an origin rather than breadcrumbing back to the wrong team.
  team: { to: "/dashboard/team", label: "My Team" },
  pto: { to: "/dashboard/pto", label: "PTO Report" },
};

const PAGE_ORIGIN_KEYS = Object.keys(PAGE_ORIGINS) as PageOriginKey[];

const ROUTE_FOR: Record<OriginKind, { to: string; param: string; fallbackLabel: string }> = {
  person: { to: "/dashboard/persons/$personId", param: "personId", fallbackLabel: "Person" },
  role: { to: "/dashboard/roles/$roleId", param: "roleId", fallbackLabel: "Role" },
  certification: {
    to: "/dashboard/certifications/$certificationId",
    param: "certificationId",
    fallbackLabel: "Certification",
  },
  skill: { to: "/dashboard/skills/$skillId", param: "skillId", fallbackLabel: "Skill" },
  competency: {
    to: "/dashboard/competencies/$competencyId",
    param: "competencyId",
    fallbackLabel: "Competency",
  },
};

/** A resolved origin: what the breadcrumb / back-link points at. */
export interface OriginDescriptor {
  /** Route path, e.g. "/dashboard/persons/$personId" or "/dashboard/organization". */
  to: string;
  /** Route params, e.g. `{ personId }`. */
  params?: Record<string, string>;
  /** Search to re-apply on the origin (e.g. `{ tab: "employees" }`, `{ view: "board" }`). */
  search?: Record<string, string>;
  /** Breadcrumb / back-link text. */
  label: string;
}

/** Search-param shape carried into a page. All optional — absent = no origin. */
export interface OriginSearch {
  /** The generic origin: base64url(JSON(OriginDescriptor)). */
  origin?: string;
  /** This page's own active tab (deep-linkable; set when returning via a back-link). */
  tab?: string;
  /**
   * The originating catalog list's filter query string (URL-encoded), carried
   * when the page was reached by clicking a row/card in a filtered list. Lets
   * the "back to catalog" crumb return to the exact filtered/sorted view.
   * Ignored when an `origin` is present.
   */
  lf?: string;
}

/** @deprecated Use {@link OriginSearch}. Kept as an alias during migration. */
export type DetailOriginSearch = OriginSearch;

function str(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * Only same-app dashboard routes may be origin targets. The token is untrusted
 * URL text, and an unvalidated `to` handed to `<Link>` renders an off-site
 * "Back" href (TanStack resolves "//evil.com" as absolute). Non-empty segments
 * of route-safe chars cover both shapes legit producers emit — templated
 * routes ("/dashboard/persons/$personId") and resolved pathnames with UUIDs.
 */
const ORIGIN_ROUTE_RE = /^\/dashboard(\/[A-Za-z0-9$_-]+)*\/?$/;

const originDescriptorSchema = z.object({
  to: z.string().regex(ORIGIN_ROUTE_RE),
  label: z.string().min(1).max(120),
  params: z.record(z.string(), z.string().max(200)).optional(),
  search: z.record(z.string(), z.string().max(2000)).optional(),
});

// Unicode-safe base64url (labels can carry "—", accented names, etc.), with no
// deprecated escape/unescape and no padding chars in the URL.
function b64urlEncode(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlDecode(s: string): string {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(padded);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Encode a descriptor into the opaque `origin` token. */
export function encodeOrigin(d: OriginDescriptor): string {
  return b64urlEncode(JSON.stringify(d));
}

/**
 * Decode an `origin` token back into a descriptor, or `null` when it's absent,
 * malformed, or fails validation. Never throws — the input is untrusted URL text.
 */
function decodeOrigin(raw: unknown): OriginDescriptor | null {
  const token = str(raw);
  if (!token) return null;
  try {
    const parsed = originDescriptorSchema.safeParse(JSON.parse(b64urlDecode(token)));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * `validateSearch` for any route that can be an origin target: keep a valid
 * `origin`, the page's own `tab`, and a carried list filter `lf`; drop the rest.
 * An `origin` takes precedence over `lf` (no point returning to a filtered
 * catalog when we know the specific place we came from).
 */
export function parseOriginSearch(search: Record<string, unknown>): OriginSearch {
  const result: OriginSearch = {};

  // Prefer the generic token; fall back to the legacy typed params so a URL
  // shared/refreshed mid-migration still resolves.
  const origin = str(search.origin) ?? encodeLegacyOrigin(search);
  if (origin) result.origin = origin;

  const tab = str(search.tab);
  if (tab) result.tab = tab;

  if (!result.origin) {
    const lf = str(search.lf);
    if (lf) result.lf = lf;
  }

  return result;
}

/** Rebuild an `origin` token from the pre-generalization typed params, if present. */
function encodeLegacyOrigin(search: Record<string, unknown>): string | undefined {
  const from = ORIGIN_KINDS.find((k) => k === search.from);
  const fromId = str(search.fromId);
  if (from && fromId) {
    const cfg = ROUTE_FOR[from];
    const fromTab = str(search.fromTab);
    return encodeOrigin({
      to: cfg.to,
      params: { [cfg.param]: fromId },
      search: fromTab ? { tab: fromTab } : undefined,
      label: str(search.fromLabel) ?? cfg.fallbackLabel,
    });
  }
  const fromPage = PAGE_ORIGIN_KEYS.find((k) => k === search.fromPage);
  if (fromPage) {
    const page = PAGE_ORIGINS[fromPage];
    return encodeOrigin({ to: page.to, label: page.label });
  }
  return undefined;
}

/**
 * Decode a carried list-filter query string (`lf`) into a search object to hand
 * a "back to catalog" `<Link>`. Returns undefined when there's nothing to
 * restore, so the crumb falls back to the pristine list route.
 */
export function decodeListSearch(lf: string | undefined): Record<string, string> | undefined {
  if (!lf) return undefined;
  const entries = Object.fromEntries(new URLSearchParams(lf));
  return Object.keys(entries).length > 0 ? entries : undefined;
}

/** Resolve an origin to a breadcrumb / back-link target, or `null` when there's none. */
export function originTarget(origin: OriginSearch): OriginDescriptor | null {
  return decodeOrigin(origin.origin);
}

/** Typed sugar: build the search a `<Link>` passes to send a record origin along. */
export function originLinkSearch(
  from: OriginKind,
  id: string,
  label?: string,
  fromTab?: string,
): OriginSearch {
  const cfg = ROUTE_FOR[from];
  return {
    origin: encodeOrigin({
      to: cfg.to,
      params: { [cfg.param]: id },
      search: fromTab ? { tab: fromTab } : undefined,
      label: label ?? cfg.fallbackLabel,
    }),
  };
}

/** Typed sugar: build the search a `<Link>` passes to return to a non-record page. */
export function pageOriginSearch(page: PageOriginKey): OriginSearch {
  const cfg = PAGE_ORIGINS[page];
  return { origin: encodeOrigin({ to: cfg.to, label: cfg.label }) };
}
